import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
export default function BikeInsuranceCalculator() {
      const [insuranceData, setInsuranceData] = useState([]);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    bikeValue: 100000,
    bikeAge: 2,
    zone: 'a',
    cubicCapacity: 150,
    ncbPercent: 0
  });
  
  const [result, setResult] = useState(null);
  const [pieData, setPieData] = useState([]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: ['bikeValue', 'bikeAge', 'cubicCapacity', 'ncbPercent'].includes(id) ? parseInt(value) : value
    });
  };

  const handleSliderChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: parseInt(value)
    });
  };

  const formatIndianCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculatePremium = () => {
    const { bikeValue, bikeAge, zone, cubicCapacity, ncbPercent } = formData;
    
    // Base rate calculation depending on bike age
    let baseRate = 0.02; // default 2%
    
    if (bikeAge <= 5) baseRate = 0.02;
    else if (bikeAge <= 10) baseRate = 0.03;
    else baseRate = 0.04;
    
    // Lower base rate for Zone B (non-metro)
    if (zone === 'b') baseRate -= 0.002;
    
    // Additional rate for higher engine capacity
    if (cubicCapacity > 350) baseRate += 0.01;
    
    // Calculate base premium
    let basePremium = bikeValue * baseRate;
    
    // Apply NCB Discount
    const ncbDiscount = basePremium * (ncbPercent / 100);
    let premiumAfterNCB = basePremium - ncbDiscount;
    
    // GST 18%
    const gst = premiumAfterNCB * 0.18;
    
    // Total premium
    const total = premiumAfterNCB + gst;
    
    // Set result
    const newResult = {
      basePremium,
      ncbDiscount,
      premiumAfterNCB,
      gst,
      total
    };
    
    setResult(newResult);
    
    // Update pie chart data
    setPieData([
      { name: 'Premium After NCB', value: premiumAfterNCB },
      { name: 'GST', value: gst }
    ]);
  };

  // Calculate on initial render
  useEffect(() => {
    calculatePremium();
  }, []);

  // Calculate whenever form data changes
  useEffect(() => {
    calculatePremium();
  }, [formData]);

  const COLORS = ['#0088FE', '#00C49F'];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: 'Is the bike insurance calculator free to use?',
        answer: 'Yes, it’s absolutely free and available online 24/7.',
      },
      {
        question: 'Can I calculate premiums for electric bikes?',
        answer: 'Yes, the calculator supports electric two-wheelers.',
      },
      {
        question: 'Is IDV customizable in the calculator?',
        answer: 'Most tools allow you to adjust IDV manually within limits.',
      },
      {
        question: 'Does it include add-ons in the quote?',
        answer: 'Yes, you can select from various add-ons and see their cost.',
      },
      {
        question: 'How is the third-party premium decided?',
        answer: 'It’s fixed by IRDAI based on engine capacity.',
      },
      {
        question: 'What if I don’t know my NCB?',
        answer: 'You can check your previous policy or use an estimated value.',
      },
      {
        question: 'Can I use the calculator for commercial bikes?',
        answer: 'Most tools focus on private bikes, but commercial options may be added.',
      },
      {
        question: 'Can I renew expired insurance using this calculator?',
        answer: 'Yes, but an inspection may be required before issuance.',
      },
      {
        question: 'Does the calculator show policies from all insurers?',
        answer: 'Depending on the platform, it may display top insurers in India.',
      },
      {
        question: 'Can I buy the policy directly from the calculator page?',
        answer: 'Yes, in most cases, you can proceed to payment after calculation.',
      },
      {
        question: 'Does it work on mobile phones?',
        answer: "Yes, it's mobile-optimized for Android and iOS devices.",
      },
      {
        question: 'How frequently should I calculate premium?',
        answer: 'Before buying or renewing your policy annually.',
      },
      {
        question: 'Is it safe to enter my vehicle details?',
        answer: 'Yes, TheFinmaster ensures complete data privacy and encryption.',
      },
      {
        question: 'Will the premium be exact or estimated?',
        answer: 'It provides a close estimate; final premium may vary slightly.',
      },
      {
        question: 'Do I need to register to use the tool?',
        answer: 'No registration or login is required.',
      },
    ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  useEffect(() => {
    fetchInsuranceData();
  }, []);
  const fetchInsuranceData = async () => {
    try {
      const response = await Http.get(Insurance_API);

      // The response structure might have nested data
      // Check if response has an insurance property (like in the original fetch response)
      const data = response.insurance || response;

      // Filter the insurance data to show only Car category
      const carInsuranceData = Array.isArray(data)
        ? data.filter((insurance) => insurance.category_name === "Bike")
        : [];

      setInsuranceData(carInsuranceData);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch insurance data");
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  return (
    <>
     <Helmet>
         <title>Bike  Insurance Plan- Bike Insurance Plan Calculator Online | TheFinmaster</title>
        {/* <meta  name='description' content='RD Calculator - Check Top Banks RD Interest Rates in India. And also know How to Calculate RD interest Rates, How to use &amp; Process of RD Calculator.'/>
        <meta name='keywords' content='rd calculator, rd deposit calculator, rd recurring deposit calculator, recurring deposit cal' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/rd-calculator" /> */}
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Bike Insurance Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
         Bike Insurance Premium Calculator
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Form Section */}
            <div className="space-y-6">
              <div>
                <label htmlFor="bikeValue" className="block text-gray-700 font-medium mb-2">
                  Bike's Insured Declared Value (IDV) (₹)
                </label>
                <input
                  type="number"
                  id="bikeValue"
                  min="10000"
                  max="1000000"
                  step="5000"
                  value={formData.bikeValue}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter bike's IDV"
                  required
                />
                <input
                  type="range"
                  id="bikeValue"
                  min="10000"
                  max="1000000"
                  step="5000"
                  value={formData.bikeValue}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>₹10,000</span>
                  <span>₹10 Lakh</span>
                </div>
              </div>

              <div>
                <label htmlFor="bikeAge" className="block text-gray-700 font-medium mb-2">
                  Bike Age (Years)
                </label>
                <input
                  type="number"
                  id="bikeAge"
                  min="0"
                  max="15"
                  value={formData.bikeAge}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter bike age"
                  required
                />
                <input
                  type="range"
                  id="bikeAge"
                  min="0"
                  max="15"
                  value={formData.bikeAge}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>New</span>
                  <span>15 Years</span>
                </div>
              </div>

              <div>
                <label htmlFor="zone" className="block text-gray-700 font-medium mb-2">
                  Zone of Registration
                </label>
                <select
                  id="zone"
                  value={formData.zone}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                >
                  <option value="a">Zone A (Metro Cities)</option>
                  <option value="b">Zone B (Non-Metro Cities)</option>
                </select>
              </div>

              <div>
                <label htmlFor="cubicCapacity" className="block text-gray-700 font-medium mb-2">
                  Engine Capacity (CC)
                </label>
                <input
                  type="number"
                  id="cubicCapacity"
                  min="50"
                  max="1500"
                  value={formData.cubicCapacity}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter engine capacity"
                  required
                />
                <input
                  type="range"
                  id="cubicCapacity"
                  min="50"
                  max="1500"
                  value={formData.cubicCapacity}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>50 CC</span>
                  <span>1500 CC</span>
                </div>
              </div>

              <div>
                <label htmlFor="ncbPercent" className="block text-gray-700 font-medium mb-2">
                  No Claim Bonus (%)
                </label>
                <input
                  type="number"
                  id="ncbPercent"
                  min="0"
                  max="50"
                  step="5"
                  value={formData.ncbPercent}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter NCB percentage"
                  required
                />
                <input
                  type="range"
                  id="ncbPercent"
                  min="0"
                  max="50"
                  step="5"
                  value={formData.ncbPercent}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                </div>
              </div>

              <button
                onClick={calculatePremium}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition duration-300"
              >
                Calculate Premium
              </button>
            </div>

            {/* Results Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Results</h2>
              
              {result && (
                <div className="space-y-6">
                  <div className="bg-white p-5 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Premium Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bike IDV:</span>
                        <span className="font-medium">{formatIndianCurrency(formData.bikeValue)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bike Age:</span>
                        <span className="font-medium">{formData.bikeAge} years</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Zone:</span>
                        <span className="font-medium">{formData.zone === 'a' ? 'Zone A (Metro)' : 'Zone B (Non-Metro)'}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Engine Capacity:</span>
                        <span className="font-medium">{formData.cubicCapacity} CC</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">NCB Percent:</span>
                        <span className="font-medium">{formData.ncbPercent}%</span>
                      </div>
                      
                      <div className="border-t border-gray-200 my-3"></div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Premium:</span>
                        <span className="font-medium">{formatIndianCurrency(result.basePremium)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">NCB Discount:</span>
                        <span className="font-medium">- {formatIndianCurrency(result.ncbDiscount)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Premium After NCB:</span>
                        <span className="font-medium">{formatIndianCurrency(result.premiumAfterNCB)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST (18%):</span>
                        <span className="font-medium">{formatIndianCurrency(result.gst)}</span>
                      </div>
                      
                      <div className="border-t border-gray-200 my-3"></div>
                      
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-gray-800">Total Annual Premium:</span>
                        <span className="text-blue-600">{formatIndianCurrency(result.total)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-5 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Premium Breakdown</h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatIndianCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
    <div className="w-full md:w-2/3 p-4 mx-auto">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Bike Insurance Premium Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            A Bike Insurance Premium Calculator is an intuitive online tool designed to help two-wheeler owners estimate the insurance premium they would need to pay for their bike insurance policy. Whether you're buying a new policy or renewing an existing one, this calculator provides quick and accurate quotes based on key details like your bike's make, model, variant, location, fuel type, and registration year.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            It simplifies the process of calculating the premium by eliminating manual guesswork and ensuring you receive real-time, insurer-aligned premium estimates. The calculator accounts for both third-party and comprehensive policies and includes add-on cover suggestions based on your profile.
          </p>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Bike Insurance Premium?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Bike Insurance Premium is the amount you pay to your insurance provider in exchange for coverage against risks related to your two-wheeler, such as theft, accidents, third-party liabilities, and natural disasters. Premiums can vary significantly depending on the type of policy:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Third-party Premium:</strong> This is mandated by law and covers liabilities towards third parties.</li>
              <li><strong>Comprehensive Premium:</strong> Includes both third-party and own-damage coverage, which protects your own vehicle against damage.</li>
              <li><strong>Add-on Covers:</strong> Options like Zero Depreciation, Roadside Assistance, Engine Protection, etc., can increase the premium but offer wider protection.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The premium amount is influenced by the vehicle’s age, location, cubic capacity (CC), IDV (Insured Declared Value), and your claim history.
            </p>
          </div>
          <div className="container mx-auto px-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
          Popular Bike Insurance Cards
            </h1>
  <div className="flex flex-col items-center space-y-6 mt-4">
    
  {insuranceData.slice(0, 5).map((insurance) => (
      <div
        key={insurance.id}
        className="w-full max-w-4xl bg-white rounded-2xl shadow hover:shadow-lg transition-shadow duration-300 p-4"
      >
        <div className="flex flex-col md:flex-row items-center md:items-start md:justify-between gap-6">
         
          <Link to={`/bike_insurance/${insurance.page_url}`}
            className="w-full md:w-[180px] flex justify-center md:justify-start"
          >
            <img
              src={insurance.image}
              alt={insurance.page_title || "Insurance Provider"}
              className="h-18 object-contain"
            />
          </Link>

   
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center md:text-left">
            <div>
              <p className="text-gray-500 text-sm font-medium">Premium Starting</p>
              <h4 className="text-lg font-bold">
              ₹{insurance.premium_start}
                
              </h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Claim Settlement</p>
              <h4 className="text-lg font-bold">{insurance.claim_settlement}%</h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Network Garages</p>
              <h4 className="text-lg font-bold">{insurance.network_garages}+</h4>
            </div>
          </div>


          <div className="flex flex-col sm:flex-row items-center gap-2 mt-4 md:mt-0">
            <a
              href={insurance.bank_link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                Get Quote!
              </button>
            </a>
             <Link to={`/bike_insurance/${insurance.page_url}`} className="w-full sm:w-auto">
              <button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                See Details
              </button>
            </Link>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use a Bike Insurance Premium Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using a bike insurance calculator is straightforward and user-friendly. Here's a detailed step-by-step breakdown:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Enter Bike Details:</strong> Choose your bike’s brand, model, variant, fuel type, and registration year.</li>
              <li><strong>Location Information:</strong> Provide the registration city or RTO.</li>
              <li><strong>Type of Insurance:</strong> Select either third-party or comprehensive policy.</li>
              <li><strong>Previous Policy Details (for renewals):</strong> Mention if there were any previous claims to determine your No Claim Bonus (NCB).</li>
              <li><strong>Add-on Covers:</strong> Choose additional covers if desired.</li>
              <li><strong>View Quote:</strong> The calculator will instantly provide a detailed breakdown of your estimated premium.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Example: How the Calculator Works
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Imagine Mr. Arun owns a 2021 Bajaj Pulsar 150 in Mumbai. He inputs the details into the calculator, opts for a comprehensive plan, and selects Zero Depreciation and Roadside Assistance as add-ons. Based on his 2 years of no claims, he gets a 25% NCB. The calculator then shows:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Base Premium: Rs. 3,200</li>
              <li>Add-ons: Rs. 900</li>
              <li>NCB Discount: -Rs. 800</li>
              <li>Total Premium Payable: Rs. 3,300</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is the Bike Insurance Premium Formula?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While the calculator automates the premium estimation, the general formula for calculating a comprehensive bike insurance premium is:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Premium = Own Damage Premium + Third-Party Premium + Add-on Costs - NCB Discount
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Own Damage Premium:</strong> Calculated based on the IDV and age of the vehicle.</li>
              <li><strong>Third-Party Premium:</strong> Regulated by the IRDAI and based on engine capacity.</li>
              <li><strong>Add-ons:</strong> Optional and based on rider preference.</li>
              <li><strong>NCB:</strong> Discount of up to 50% for claim-free years.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Information Required for Premium Calculation
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To get accurate results from a bike insurance calculator, you must enter the following details:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Bike Make, Model, Variant</li>
              <li>Fuel Type (Petrol/Electric)</li>
              <li>Registration Year & RTO Location</li>
              <li>Vehicle Cubic Capacity (CC)</li>
              <li>Previous Claim History (if applicable)</li>
              <li>Type of Insurance (Third-party or Comprehensive)</li>
              <li>Preferred Add-ons (optional)</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Benefits of Bike Insurance Premium Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant Quotes:</strong> Provides real-time premium estimates within seconds.</li>
              <li><strong>Saves Time:</strong> Eliminates the need for paperwork or physical visits.</li>
              <li><strong>Customizable Plans:</strong> Lets you explore add-ons and tailor your policy.</li>
              <li><strong>Error-Free Estimations:</strong> Reduces chances of human miscalculation.</li>
              <li><strong>Informed Decisions:</strong> Helps compare different insurers for better choices.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is Bike Insurance Premium Determined?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several factors affect your bike insurance premium:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Insured Declared Value (IDV):</strong> Higher IDV = higher premium.</li>
              <li><strong>Age of the Bike:</strong> Older bikes have lower IDV and premiums.</li>
              <li><strong>Cubic Capacity:</strong> Higher CC = higher third-party liability premium.</li>
              <li><strong>RTO Location:</strong> Urban areas attract higher premiums than rural.</li>
              <li><strong>No Claim Bonus (NCB):</strong> More claim-free years = more discount.</li>
              <li><strong>Type of Policy:</strong> Comprehensive policies cost more than third-party.</li>
              <li><strong>Add-on Covers:</strong> Increase the overall premium.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Reduce Bike Insurance Premium?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To reduce your premium without compromising on coverage:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Opt for a higher voluntary deductible.</li>
              <li>Install anti-theft devices certified by ARAI.</li>
              <li>Maintain a claim-free record to earn NCB.</li>
              <li>Avoid unnecessary add-ons.</li>
              <li>Renew policy on time to retain NCB.</li>
              <li>Compare quotes across insurers before purchase.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefit of Bike Insurance
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Currently, individuals do not receive any tax benefit under Section 80C or 80D for paying bike insurance premiums. Bike insurance is considered a financial protection tool but not a tax-saving investment. However, for commercial vehicles, businesses can claim the insurance premium as a business expense under income tax filings.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinmaster Bike Insurance Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>AI-Enabled Suggestions:</strong> Our calculator recommends ideal policies and add-ons based on your inputs.</li>
              <li><strong>Instant Access:</strong> View quotes from multiple insurers without switching tabs.</li>
              <li><strong>Secure & Private:</strong> No login or data sharing required.</li>
              <li><strong>Tailored to Indian Market:</strong> Supports all major Indian bike brands and insurers.</li>
              <li><strong>Renewal Friendly:</strong> Easy to switch or renew policies with NCB preservation.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Calculate Bike Insurance Premium for New vs Old Bikes
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>For New Bikes:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Use ex-showroom price to estimate IDV.</li>
              <li>Premiums are higher due to no depreciation.</li>
              <li>Select new policy type and add-ons as required.</li>
              <li>No NCB available in the first year.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong> A new 2024 Royal Enfield Classic 350 may have an IDV of Rs. 2.2 lakh. Premium might be:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Base OD: Rs. 4,500</li>
              <li>Third-party: Rs. 2,300</li>
              <li>Add-ons: Rs. 1,200</li>
              <li>Total: ~Rs. 8,000</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>For Old Bikes:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Adjusted IDV based on depreciation rate (typically 5-50%).</li>
              <li>Include previous claim history and NCB.</li>
              <li>Fewer relevant add-ons.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong> A 2017 Hero Splendor IDV may drop to Rs. 35,000. With a 50% NCB, the premium may fall to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>OD: Rs. 850</li>
              <li>Third-party: Rs. 1,100</li>
              <li>Add-ons: Rs. 300</li>
              <li>NCB: -Rs. 425</li>
              <li>Total: ~Rs. 1,825</li>
            </ul>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
               <Link key={category.id} to={`/category/${category.id}`}>
                 <li className="py-2 border-b border-gray-300">{category.name}</li>
               </Link>
             ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
                  <Link key={category.id} to={`/category/${category.id}`}>
                    <li className="py-2 border-b border-gray-300">{category.name}</li>
                  </Link>
                ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
</>
  );
}